import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { ApiRoute, BffInput } from "@slfinrtl/ui-bff-routes"
import { useCallback } from "react"
import { callBff } from "../../common/callBff"
import type { ErrorNotificationType } from "../useErrorNotificationCallback/useErrorNotificationCallback"
import { useErrorNotificationCallback } from "../useErrorNotificationCallback/useErrorNotificationCallback"
import { useExtractErrorMessage } from "../useExtractErrorMessage/useExtractErrorMessage"
import { useJsonMemorizedValue } from "../useJsonMemorizedValue/useJsonMemorizedValue"

/**
 * @param route The Bff route to call. Usually imported via ~routes
 * @param input Contains all pathparams, queryparams and request body
 * @param errorNotificationType Controls how to display errormessages. Every
 * error will still be thrown to enable additional errorhandling from outside. Can be one of the following values:
 * "inline": An inline notification will be displayed. In this case, the
 *      corresponding Container must be wrapped with the withErrorHandler HOC and use
 *      <ErrorNotificationInline /> component to display the error.
 * "global": A global error banner will be displayed on top of the page
 * "none": Errors will not be handled by this hook.
 *
 * @returns () => Promise<ResponseType>
 *
 */
export const useBff = <RequestPathParams, RequestQueryParams, RequestBody, ResponseType>(
	route: ApiRoute<RequestPathParams, RequestQueryParams, RequestBody, ResponseType>,
	input: BffInput<RequestPathParams, RequestQueryParams, RequestBody>,
	errorNotificationType: ErrorNotificationType,
): (() => Promise<ResponseType>) => {
	const { dealershipId, environment, getAuthorizationHeader, locale, marketplaceKey } = useDealerContext()
	const memorizedInput = useJsonMemorizedValue(input)

	const extractErrorMessage = useExtractErrorMessage()
	const errorNotificationCallback = useErrorNotificationCallback(errorNotificationType)

	return useCallback(() => {
		return callBff(
			{
				dealershipId,
				environment: environment ?? "local",
				getAuthorizationHeader,
				locale,
				marketplaceKey,
			},
			route,
			memorizedInput,
		)
			.then((value) => {
				errorNotificationCallback?.(undefined)
				return value
			})
			.catch((error: unknown) => {
				errorNotificationCallback?.(extractErrorMessage(error))
				throw error
			})
	}, [
		route,
		getAuthorizationHeader,
		locale,
		environment,
		dealershipId,
		marketplaceKey,
		memorizedInput,
		extractErrorMessage,
		errorNotificationCallback,
	])
}
